import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useLoginMutation } from "../features/auth/authApiSlice";
import {
  selectCurrentToken,
  selectCurrentUser,
  setCredentials,
} from "../features/auth/authSlice";

export const Login = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState<
    { email: string; password: string } | null
  >(null);

  // hooks call
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);

  useEffect(() => {
    if (token && currentUser) {
      navigate("/app/home");
    }
  }, [currentUser, token]);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Please type your password"),
    rememberMe: Yup.boolean(),
  });

  const loginHandler = async (
    email: string,
    password: string,
    code?: string
  ): Promise<boolean> => {
    try {
      const userData: any = await login({ email, password, code }).unwrap();
      const { token, refreshToken, ...user } = userData;
      dispatch(setCredentials({ token, user, refreshToken }));
      navigate("/app/home");
      return true;
    } catch (error: any) {
      console.log(error);
      toast.dismiss();
      // Clear previous inline errors
      setServerError(null);
      
      // Get error details
      const statusFromError =
        error?.statusCode || error?.status || error?.data?.statusCode;
      const messageFromError =
        (typeof error?.message === "string" && error?.message) ||
        (typeof error?.data?.message === "string" && error?.data?.message) ||
        "";

      // Handle email verification error
      if (
        statusFromError === 401 &&
        messageFromError.includes("Email is not verified")
      ) {
        navigate(`/verify-email?email=${email}`);
        return false;
      }

      // If we already have OTP modal open and get error, show it (wrong OTP)
      if (showOtpModal) {
        const backendMessage: any = error?.message ?? error?.data?.message;
        const messageString = Array.isArray(backendMessage)
          ? backendMessage.join("\n")
          : (typeof backendMessage === "string"
              ? backendMessage
              : "An error occurred. Please try again.");
        setServerError(messageString);
        toast.error(messageString);
        return false;
      }

      // OTP required (first time login requiring OTP)
      if (statusFromError === 422) {
        setPendingCredentials({ email, password });
        setShowOtpModal(true);
        return false;
      }

      // Show backend errors inline and via toast
      const backendMessage: any = error?.message ?? error?.data?.message;
      const messageString = Array.isArray(backendMessage)
        ? backendMessage.join("\n")
        : (typeof backendMessage === "string"
            ? backendMessage
            : "An error occurred. Please try again.");
      setServerError(messageString);
      toast.error(messageString);
      return false;
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      setServerError(null);
      loginHandler(values.email, values.password);
    },
  });

  return (
    <div className="login-bg1 w-full font-dashboard bg-[#f8f8f8] h-screen flex justify-center items-center">
      <div className="bg-white w-full max-w-[400px] rounded-md shadow-lg px-5 py-10">
        <h3 className="text-2xl text-indigo-500 font-semibold text-center">
          Gcbuying
        </h3>

        <h4 className="text-lg font-[500] text-slate-600 mt-8">
          Welcome to Gcbuying ! 👋
        </h4>
        <p className="text-sm text-slate-500 mt-1">
          Sign in now to start trading
        </p>
        {serverError && (
          <div className="mt-4 text-sm text-red-600 whitespace-pre-line">
            {serverError}
          </div>
        )}
        <form action="" className="mt-6" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-xs text-slate-600">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder="john@example.com"
              className="transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <p className="text-red-500 text-xs">
              {formik.touched.email &&
                Boolean(formik.errors.email) &&
                formik.errors.email}
            </p>
          </div>
          <div className="flex flex-col gap-1 mt-4">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-xs text-slate-600">
                Password
              </label>
              <Link to="/password/reset" className="text-xs text-indigo-500">
                Forgot Password?
              </Link>
            </div>

            <div className="flex items-center overflow-hidden pl-4 justify-between border border-slate-300 rounded-md focus:ring-1 focus:ring-indigo-500">
              <input
                type={isPasswordVisible ? "text" : "password"}
                name="password"
                id="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                placeholder="••••••"
                className="w-full bg-transparent py-2 text-sm focus:outline-none"
              />
              {isPasswordVisible ? (
                <AiOutlineEye
                  className="text-lg text-slate-500 box-content px-3 py-2 cursor-pointer"
                  onClick={() => setIsPasswordVisible(false)}
                />
              ) : (
                <AiOutlineEyeInvisible
                  className="text-lg text-slate-500 box-content px-3 py-2 cursor-pointer"
                  onClick={() => setIsPasswordVisible(true)}
                />
              )}
            </div>
            <p className="text-red-500 text-xs">
              {formik.touched.password &&
                Boolean(formik.errors.password) &&
                formik.errors.password}
            </p>
          </div>

          <div className="flex items-center gap-1 mt-3">
            <input
              type="checkbox"
              name="rememberMe"
              id="rememberMe"
              checked={formik.values.rememberMe}
              onChange={formik.handleChange}
            />
            <label htmlFor="rememberMe" className="text-sm text-slate-500">
              Remember Me
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="mt-4 transition-all duration-200 w-full py-2 text-sm rounded-md text-white font-semibold bg-indigo-500 hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </button>

          <p className="text-sm text-center text-slate-500 mt-5">
            New on our platform?{" "}
            <Link to="/register" className="text-sm text-indigo-500">
              Create an account
            </Link>
          </p>
        </form>
      </div>

      {showOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-md bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800">Two-step verification</h3>
            <p className="mt-2 text-sm text-slate-600">
              Enter the 6-digit code sent to your email to continue.
            </p>

            <form
              className="mt-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!pendingCredentials) return;
                setServerError(null);
                try {
                  const ok = await loginHandler(
                    pendingCredentials.email,
                    pendingCredentials.password,
                    otpCode
                  );
                  if (ok) {
                    setShowOtpModal(false);
                    setOtpCode("");
                    setPendingCredentials(null);
                  }
                } catch (e) {
                  // handled in loginHandler
                }
              }}
            >
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                value={otpCode}
                onChange={(e) => {
                  const numeric = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                  setOtpCode(numeric);
                }}
                placeholder="123456"
                className="w-full rounded-md border border-slate-300 p-3 text-center text-lg tracking-widest focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              {serverError && (
                <div className="mt-3 text-sm text-red-600 whitespace-pre-line">
                  {serverError}
                </div>
              )}
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtpCode("");
                  }}
                  className="w-1/2 rounded-md border border-slate-300 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={otpCode.length !== 6 || isLoading}
                  className="w-1/2 rounded-md bg-indigo-500 py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Verifying..." : "Verify"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
